/**
 * 导出共享样式和工具函数
 * 用于 PDF、图片等导出服务
 */

import { marked } from 'marked'
import katex from 'katex'

/**
 * 共享的 Markdown 渲染样式
 */
export const EXPORT_STYLES = `
  h1, h2, h3, h4, h5, h6 {
    margin-top: 24px;
    margin-bottom: 16px;
    font-weight: 600;
    line-height: 1.25;
  }
  h1 { font-size: 2em; }
  h2 { font-size: 1.5em; }
  h3 { font-size: 1.25em; }
  h4 { font-size: 1em; }
  h5 { font-size: 0.875em; }
  h6 { font-size: 0.85em; color: #6a737d; }
  p { margin-bottom: 16px; }
  a { color: #0366d6; text-decoration: none; }
  a:hover { text-decoration: underline; }
  pre {
    background: #f6f8fa;
    border-radius: 6px;
    padding: 16px;
    overflow: auto;
    font-size: 85%;
    line-height: 1.45;
    margin-bottom: 16px;
  }
  code {
    background: rgba(27, 31, 35, 0.05);
    border-radius: 3px;
    font-size: 85%;
    margin: 0;
    padding: 0.2em 0.4em;
  }
  pre > code { background: transparent; padding: 0; }
  blockquote {
    border-left: 4px solid #dfe2e5;
    padding: 0 16px;
    color: #6a737d;
    margin: 0 0 16px 0;
  }
  ul, ol { padding-left: 2em; margin-bottom: 16px; }
  li { margin-bottom: 0.25em; }
  table {
    border-collapse: collapse;
    border-spacing: 0;
    width: 100%;
    margin-bottom: 16px;
  }
  table th, table td { border: 1px solid #dfe2e5; padding: 6px 13px; }
  table th { background: #f6f8fa; font-weight: 600; }
  table tr:nth-child(2n) { background: #f6f8fa; }
  img { max-width: 100%; height: auto; margin: 0 auto; display: block; }
  hr { border: none; border-top: 1px solid #eaecef; height: 1px; margin: 24px 0; }
  .math-formula {
    text-align: center;
    margin: 16px 0;
    font-family: 'Times New Roman', serif;
    font-style: italic;
  }
  @media print {
    .no-print { display: none; }
  }
`

/**
 * 共享的容器基础样式
 */
export const CONTAINER_STYLES = {
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
  lineHeight: '1.6',
  color: '#333',
  backgroundColor: 'white',
}

/**
 * 配置 marked 解析器
 */
export const configureMarked = () => {
  marked.setOptions({
    breaks: true,
    gfm: true,
  })
  const renderer = new marked.Renderer()
  marked.use({ renderer })
}

/**
 * 数学公式占位符存储（导出用）
 */
const mathPlaceholders: Map<string, { formula: string; displayMode: boolean }> = new Map()
let placeholderCounter = 0

/**
 * 生成唯一占位符
 */
const generatePlaceholder = (): string => {
  return `%%MATH_PLACEHOLDER_${placeholderCounter++}%%`
}

/**
 * 预处理 Markdown，转换 LaTeX 数学公式格式并提取公式
 */
const preprocessAndExtractMath = (content: string): string => {
  if (!content) return ''

  mathPlaceholders.clear()
  placeholderCounter = 0

  let processed = content

  // 转换 LaTeX 数学公式格式为标准格式
  // \[...\] -> $$...$$（块级公式）
  processed = processed.replace(/\\\[([\s\S]*?)\\\]/g, '$$$$$1$$$$')

  // \(...\) -> $...$（行内公式）
  processed = processed.replace(/\\\(([\s\S]*?)\\\)/g, '$$$1$$')

  // 提取块级公式 $$...$$
  processed = processed.replace(/\$\$([\s\S]*?)\$\$/g, (_match, formula) => {
    const placeholder = generatePlaceholder()
    mathPlaceholders.set(placeholder, { formula: formula.trim(), displayMode: true })
    return `\n\n${placeholder}\n\n`
  })

  // 提取行内公式 $...$
  processed = processed.replace(/\$([^\$\n]+?)\$/g, (_match, formula) => {
    const placeholder = generatePlaceholder()
    mathPlaceholders.set(placeholder, { formula: formula.trim(), displayMode: false })
    return placeholder
  })

  return processed
}

/**
 * 将占位符替换回渲染后的数学公式
 */
const restoreMathFormulas = (html: string): string => {
  let result = html

  mathPlaceholders.forEach((data, placeholder) => {
    try {
      const rendered = katex.renderToString(data.formula, {
        displayMode: data.displayMode,
        throwOnError: false,
        strict: false,
      })
      const wrapper = data.displayMode
        ? `<div class="math-block" style="text-align: center; margin: 1em 0; overflow-x: auto;">${rendered}</div>`
        : `<span class="math-inline">${rendered}</span>`
      result = result.replace(new RegExp(placeholder, 'g'), wrapper)
    } catch (error) {
      console.warn('KaTeX 渲染错误:', error)
      const errorHtml = `<span style="color: red;">${data.formula}</span>`
      result = result.replace(new RegExp(placeholder, 'g'), errorHtml)
    }
  })

  return result
}

/**
 * 将 Markdown 转换为 HTML
 */
export const parseMarkdown = (content: string): string => {
  configureMarked()
  const withPlaceholders = preprocessAndExtractMath(content)
  const html = marked.parse(withPlaceholders) as string
  return restoreMathFormulas(html)
}

/**
 * 生成标题 HTML
 */
export const generateTitleHtml = (title?: string): string => {
  if (!title) return ''
  return `<h1 style="text-align: center; margin-bottom: 30px; color: #333;">${title}</h1>`
}

/**
 * 等待容器渲染完成
 * 使用 requestAnimationFrame 和图片/字体加载检测，比固定延时更可靠
 */
export const waitForRender = (container: HTMLElement, maxWait = 500): Promise<void> => {
  return new Promise((resolve) => {
    const startTime = Date.now()

    const checkReady = () => {
      // 检查所有图片是否加载完成
      const images = container.querySelectorAll('img')
      const allImagesLoaded = Array.from(images).every(img => img.complete)

      // 检查容器是否有有效尺寸
      const hasSize = container.offsetWidth > 0 && container.offsetHeight > 0

      // 检查是否超时
      const elapsed = Date.now() - startTime

      if ((allImagesLoaded && hasSize) || elapsed >= maxWait) {
        // 额外等待一帧确保渲染完成
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            resolve()
          })
        })
      } else {
        requestAnimationFrame(checkReady)
      }
    }

    // 开始检查
    requestAnimationFrame(checkReady)
  })
}

/**
 * 生成导出文件名
 * @param extension 文件扩展名
 * @param customFilename 自定义文件名（如果提供则优先使用）
 */
export const generateExportFilename = (
  extension: string,
  customFilename?: string
): string => {
  if (customFilename) {
    // 如果自定义文件名已包含扩展名，直接返回
    if (customFilename.toLowerCase().endsWith(`.${extension}`)) {
      return customFilename
    }
    return `${customFilename}.${extension}`
  }

  // 使用默认名称
  const baseName = 'Markflow'

  // 清理文件名中的非法字符
  const safeName = baseName
    .replace(/[<>:"/\\|?*]/g, '_')  // 替换非法字符
    .replace(/\s+/g, '_')           // 替换空格
    .substring(0, 100)              // 限制长度

  return `${safeName}.${extension}`
}
